import { type SyntheticEvent, useEffect, useRef } from 'react';

import { useConsentActions, useConsentStatus } from '@/ampli';
import { BudgyMark } from '@/components/BudgyWordmark';
import { privacyPolicyPath } from '@/utils/privacy-policy';

/**
 * Client-only consent dialog. Performance and SEO constraints:
 * - Never renders during SSR (consent is `pending`), so crawled HTML is unchanged.
 * - Native `<dialog>` sits in the top layer without a scroll lock, so the
 *   scrollbar never disappears and nothing shifts (no CLS).
 * - Copy stays short and small so it can't outgrow page headings as the
 *   Largest Contentful Paint element.
 */
const ConsentBanner = () => {
  const status = useConsentStatus();
  const { handleAccept, handleDecline } = useConsentActions();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isUndecided = status === 'undecided';

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!isUndecided || !dialog || dialog.open) {
      return;
    }

    dialog.showModal();
  }, [isUndecided]);

  if (!isUndecided) {
    return null;
  }

  return (
    <dialog
      aria-describedby="consent-description"
      aria-labelledby="consent-heading"
      className="m-auto w-[min(560px,calc(100%-24px))] max-w-none overflow-hidden rounded-[24px] border-0 bg-white p-0 text-ink shadow-card-hover transition-[opacity,translate] duration-300 ease-out backdrop:bg-ink/45 backdrop:transition-[opacity] backdrop:duration-300 starting:translate-y-3 starting:opacity-0 starting:backdrop:opacity-0 motion-reduce:transition-none"
      id="consent-banner"
      onCancel={preventDismiss}
      ref={dialogRef}
    >
      <div className="flex items-center justify-between gap-4 border-b border-line-subtle px-6 py-4 sm:px-8">
        <BudgyMark />
        <span className="rounded-full bg-chip px-2.5 py-1 text-[13px] text-ink-muted">
          Cookie settings
        </span>
      </div>
      <div className="flex flex-col gap-2 px-6 pt-6 pb-2 sm:px-8 sm:pt-7">
        <h2
          className="m-0 text-xl font-medium tracking-[-0.01em]"
          id="consent-heading"
        >
          We use cookies
        </h2>
        <p
          className="m-0 text-[15px] leading-[1.55] text-ink-soft"
          id="consent-description"
        >
          Analytics cookies help us understand how the site is used and improve
          it. They only load if you accept. Read our{' '}
          <a
            className="text-ink underline underline-offset-[3px] hover:text-ink-muted"
            href={privacyPolicyPath}
          >
            privacy policy
          </a>
          .
        </p>
      </div>
      <div className="flex flex-col-reverse gap-3 px-6 pt-5 pb-6 sm:flex-row sm:justify-end sm:px-8 sm:pb-8">
        <button
          className="h-12 cursor-pointer rounded-xl border border-line-strong bg-white px-6 text-base font-medium text-ink transition-[background-color] duration-150 ease-out hover:bg-field"
          onClick={handleDecline}
          type="button"
        >
          Decline
        </button>
        <button
          className="button-primary h-12 px-6 text-base"
          onClick={handleAccept}
          type="button"
        >
          Accept all
        </button>
      </div>
    </dialog>
  );
};

const preventDismiss = (event: SyntheticEvent<HTMLDialogElement>) => {
  event.preventDefault();
};

export default ConsentBanner;
